import React from 'react';
import { Link } from 'react-router-dom';
import { SectionCard } from '../../layout/memberPrimitives';
import { formatDateLabel } from '../../../utils/dateLabels';
import type { DueActionCard } from '../../../utils/memberEngagement';

/**
 * "What we're watching" — the released surveillance plan.
 *
 * The wording matters as much as the data. This is a clinician-approved
 * monitoring plan, not a generated to-do list, and the footnote says so
 * explicitly: members who read it as software output will discount it.
 */
export default function WatchlistSection({
  items,
  planReleasedAt,
}: {
  items: DueActionCard[];
  planReleasedAt: Date | null;
}) {
  if (items.length === 0) {
    return (
      <SectionCard eyebrow="Your plan" title="Not written yet">
        <p className="text-sm mb-3" style={{ color: 'var(--melio-text-muted)' }}>
          Your monitoring plan is written after your first clinician review, once your baseline is in.
        </p>
        <Link to="/reports/sample" className="text-xs underline" style={{ color: 'var(--melio-olive-deep)' }}>
          See a sample report →
        </Link>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      eyebrow="Your plan"
      title={`${items.length} area${items.length === 1 ? '' : 's'} we're watching`}
      rightSlot={
        <Link to="/plan" className="text-xs underline" style={{ color: 'var(--melio-text-muted)' }}>
          View your plan →
        </Link>
      }
    >
      <ul className="space-y-2 list-none p-0 m-0">
        {items.map((item) => (
          <li
            key={item.id}
            className="rounded-xl border px-3 py-2.5"
            style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--melio-cream-warm)' }}
          >
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <p className="text-sm font-medium" style={{ color: 'var(--melio-ink)' }}>
                {item.title}
              </p>
              <span className="text-[11px] whitespace-nowrap" style={{ color: 'var(--melio-text-muted)' }}>
                {item.status}
              </span>
            </div>
            {item.detail && (
              <p className="text-xs mt-1" style={{ color: 'var(--melio-text-muted)' }}>
                {item.detail}
              </p>
            )}
          </li>
        ))}
      </ul>
      <p className="text-[11px] mt-3" style={{ color: 'var(--melio-text-muted)' }}>
        Written and signed off by your care team
        {planReleasedAt ? ` on ${formatDateLabel(planReleasedAt)}` : ''}.
      </p>
    </SectionCard>
  );
}
